import { Component, inject, signal } from '@angular/core';
import { MatCardModule } from '@angular/material/card';
import { MatTableModule } from '@angular/material/table';
import { MatChipsModule } from '@angular/material/chips';
import { MatButtonModule } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { FormsModule } from '@angular/forms';
import { switchMap } from 'rxjs/operators';
import { CommentComponent } from '../../../../../shared/ui/comment-component/comment-component';
import { DocumentUploadComponent, DocumentFileEntry } from '../../../../../shared/ui/document-upload/document-upload';
import { SortCommentsDescPipe } from '../../../../../shared/pipes/sort-comments-desc.pipe';
import { SnackbarService } from '../../../../../services/snackbar.service';
import { PendingCheckFacadeStore } from '../../store/pending-check-facade.store';
import { PendingCheckPermissionService } from '../../../services/pending-check-permission';
import { ProjectService } from '../../../../../core/projects/project';
import { CheckService } from '../../../../../core/checks/check';
import {DocumentService} from '../../../../../core/documents/document';

@Component({
  selector: 'app-document-comments-card',
  imports: [
    MatCardModule,
    MatTableModule,
    MatChipsModule,
    MatButtonModule,
    MatIcon,
    MatFormFieldModule,
    MatInputModule,
    FormsModule,
    CommentComponent,
    DocumentUploadComponent,
    SortCommentsDescPipe,
  ],
  templateUrl: './document-comments-card.html',
  styleUrl: './document-comments-card.scss',
})
export class DocumentCommentsCard {
  readonly data = inject(PendingCheckFacadeStore);
  readonly permission = inject(PendingCheckPermissionService);
  private readonly snackbarService = inject(SnackbarService);
  private readonly projectService = inject(ProjectService);
  private readonly documentService = inject(DocumentService);
  private readonly checkService = inject(CheckService);

  readonly displayedColumns: string[] = ['path', 'document', 'comments'];

  readonly activeCommentDocId = signal<string | null>(null);

  toggleCommentInput(docId: string): void {
    this.activeCommentDocId.update(current => current === docId ? null : docId);
  }

  closeCommentInput(): void {
    this.activeCommentDocId.set(null);
  }

  isCommentInputOpen(docId: string): boolean {
    return this.activeCommentDocId() === docId;
  }

  async addComment(contentInput: HTMLInputElement, documentGuid: string, commentType: number): Promise<void> {
    if (!contentInput.value.trim()) return;
    await this.data.addDocumentComment(documentGuid, contentInput.value.trim(), commentType === 2 ? 'INFO' : 'WARNING');
    contentInput.value = '';
  }

  // --- Kommentar-Event-Handler ---

  onCommentDoneToggled(event: { guid: string; isDone: boolean }): void {
    this.data.updateCommentStatus(event.guid, event.isDone);
  }

  onCommentSeverityToggled(event: { guid: string; severity: string }): void {
    this.data.setCommentSeverity(event.guid, event.severity);
  }

  onCommentDeleted(guid: string): void {
    this.data.deleteComment(guid);
  }

  // --- Dokument-Aktionen ---

  onDocumentsAdded(entries: DocumentFileEntry[]): void {
    const currentCheck = this.data.currentCheck();
    const projectCode = this.data.projectCode();
    if (!currentCheck || !projectCode) return;

    for (const entry of entries) {
      this.projectService.createDocumentForProjectApi(projectCode, { displayName: entry.name, uri: entry.uri })
        .pipe(
          switchMap(newDoc =>
            this.checkService.linkDocumentApi(currentCheck.id, newDoc.guid)
          )
        )
        .subscribe({
          next: () => {
            this.data.refreshDocuments();
            this.snackbarService.success(`Dokument „${entry.name}" hinzugefügt`);
          },
          error: err => {
            console.error('Dokument verknüpfen fehlgeschlagen', err);
            this.snackbarService.warning(
              `Dokument "${entry.name}" konnte nicht verknüpft werden.`,
              { message: 'Das Dokument wurde im Projekt erstellt, aber nicht mit der Kontrolle verknüpft.' }
            );
          },
        });
    }
  }

  removeDocumentFromCheck(_documentGuid: string): void {
    this.documentService.softDeleteDocument(_documentGuid).subscribe({
      next: () => {
        this.data.refreshDocuments();
        this.snackbarService.success(`Dokument gelöscht`);
      },
      error: err => {
        console.error('Dokument löschen fehlgeschlagen', err);
        this.snackbarService.warning(
          `Dokument konnte nicht gelöscht werden.`,
          { message: 'Das Dokument wurde nicht gelöscht. Bitte versuchen Sie es erneut.' }
        );
      }
    })
  }
}
